import * as React from 'react';
import { ROUTES } from '@/common/auth-routes';
import { ValidationStatus } from '@/features/onboarding/hooks/use-token-validation';
import type { TokenValidationResult } from '@/features/onboarding/types/onboarding.types';
import { OnboardingPageAlreadyCompleted } from './onboarding-page-already-completed';
import { OnboardingPageError } from './onboarding-page-error';
import { OnboardingPageExpired } from './onboarding-page-expired';
import { OnboardingPageLoading } from './onboarding-page-loading';

interface OnboardingTokenValidationStatusScreenProps {
  status: ValidationStatus;
  validationResult: TokenValidationResult | null;
  error: string | null;
  /** Builds the sign-in URL given a tenant slug (differs per page context) */
  buildSignInUrl: (tenantSlug: string) => string;
}

/**
 * Renders the appropriate full-page state for each non-Valid validation status.
 * Callers should guard with `status !== ValidationStatus.VALID` before rendering.
 */
export function OnboardingTokenValidationStatusScreen({
  status,
  validationResult,
  error,
  buildSignInUrl,
}: OnboardingTokenValidationStatusScreenProps): React.ReactElement | null {
  if (status === ValidationStatus.VALIDATING) {
    return <OnboardingPageLoading />;
  }

  if (status === ValidationStatus.ALREADY_COMPLETED) {
    const tenantSlug = validationResult?.tenant_slug;

    return (
      <OnboardingPageAlreadyCompleted
        signInUrl={
          tenantSlug ? buildSignInUrl(tenantSlug) : ROUTES.TENANT_SELECTION
        }
        userEmail={validationResult?.user_email}
      />
    );
  }

  if (status === ValidationStatus.EXPIRED) {
    return <OnboardingPageExpired />;
  }

  if (status === ValidationStatus.INVALID) {
    return <OnboardingPageError error={error} />;
  }

  return null;
}
